// src/pages/dropshipping/DropshippingSettingsPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Save, Upload, CheckCircle, XCircle, Clock, AlertCircle, ChevronDown, Copy, Link, RefreshCw, Lock } from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import {
  getDropshippingSettings,
  updateDropshippingSettings,
  uploadKycDocuments,
  getBanks,
  getBankBranches,
  generateInviteLink,
} from '../../services/api/dropshipping';
import ProviderProductsTab from './ProviderProductsTab';
import styles from './DropshippingSettingsPage.module.css';

const PAYMENT_CYCLES = [1, 3, 7, 14, 30];

const KYC_STATUS_CONFIG = {
  not_submitted: { icon: AlertCircle, className: 'kycNotSubmitted', label: 'kycNotSubmitted' },
  pending: { icon: Clock, className: 'kycPending', label: 'kycPending' },
  approved: { icon: CheckCircle, className: 'kycApproved', label: 'kycApproved' },
  rejected: { icon: XCircle, className: 'kycRejected', label: 'kycRejected' },
};

const DropshippingSettingsPage = () => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const [activeTab, setActiveTab] = useState('provider');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    isProviderEnabled: false,
    providerListingVisibility: 'private',
    providerServiceCharge: 0,
    providerContactPhone: '',
    providerAllowedPaymentCycles: [7],
    isResellerEnabled: false,
    resellerContactPhone: '',
    resellerBankId: null,
    resellerBankBranchId: null,
    resellerBankAccountName: '',
    resellerBankAccountNumber: '',
  });
  const [kycFiles, setKycFiles] = useState({ kycNicFront: null, kycNicBack: null, kycBusinessRegImage: null });
  const [kycPreviews, setKycPreviews] = useState({ kycNicFront: null, kycNicBack: null, kycBusinessRegImage: null });
  const [generatingLink, setGeneratingLink] = useState(false);
  const nicFrontRef = useRef();
  const nicBackRef = useRef();
  const bizRegRef = useRef();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (form.resellerBankId) {
      getBankBranches(form.resellerBankId).then(setBranches).catch(() => setBranches([]));
    } else {
      setBranches([]);
    }
  }, [form.resellerBankId]);

  const loadData = async () => {
    try {
      const [settingsData, banksData] = await Promise.all([getDropshippingSettings(), getBanks()]);
      setSettings(settingsData);
      setBanks(banksData);
      setForm({
        isProviderEnabled: settingsData.isProviderEnabled || false,
        providerListingVisibility: settingsData.providerListingVisibility || 'private',
        providerServiceCharge: settingsData.providerServiceCharge || 0,
        providerContactPhone: settingsData.providerContactPhone || '',
        providerAllowedPaymentCycles: settingsData.providerAllowedPaymentCycles || [7],
        isResellerEnabled: settingsData.isResellerEnabled || false,
        resellerContactPhone: settingsData.resellerContactPhone || '',
        resellerBankId: settingsData.resellerBankId || null,
        resellerBankBranchId: settingsData.resellerBankBranchId || null,
        resellerBankAccountName: settingsData.resellerBankAccountName || '',
        resellerBankAccountNumber: settingsData.resellerBankAccountNumber || '',
      });
      if (settingsData.resellerBankId) {
        const branchData = await getBankBranches(settingsData.resellerBankId);
        setBranches(branchData);
      }
    } catch (err) {
      showError(t('dropshipping.failedToSave'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const togglePaymentCycle = (cycle) => {
    setForm((prev) => {
      const current = prev.providerAllowedPaymentCycles || [];
      if (current.includes(cycle)) {
        return { ...prev, providerAllowedPaymentCycles: current.filter((c) => c !== cycle) };
      }
      return { ...prev, providerAllowedPaymentCycles: [...current, cycle].sort((a, b) => a - b) };
    });
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateDropshippingSettings(form);
      success(t('dropshipping.settingsSaved'));
      loadData();
    } catch (err) {
      showError(err.message || t('dropshipping.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateInviteLink = async () => {
    setGeneratingLink(true);
    try {
      await generateInviteLink();
      await loadData();
      success(t('dropshipping.settingsSaved'));
    } catch (err) {
      showError(err.message || t('dropshipping.failedToSave'));
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyInviteLink = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      success(t('dropshipping.inviteLinkCopied'));
    });
  };

  const handleKycFileChange = (field, file) => {
    if (kycPreviews[field]) URL.revokeObjectURL(kycPreviews[field]);
    const previewUrl = file ? URL.createObjectURL(file) : null;
    setKycFiles((prev) => ({ ...prev, [field]: file || null }));
    setKycPreviews((prev) => ({ ...prev, [field]: previewUrl }));
  };

  const handleKycUpload = async () => {
    if (!kycFiles.kycNicFront || !kycFiles.kycNicBack) {
      showError(t('dropshipping.errorNicRequired'));
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      if (kycFiles.kycNicFront) fd.append('kycNicFront', kycFiles.kycNicFront);
      if (kycFiles.kycNicBack) fd.append('kycNicBack', kycFiles.kycNicBack);
      if (kycFiles.kycBusinessRegImage) fd.append('kycBusinessRegImage', kycFiles.kycBusinessRegImage);
      await uploadKycDocuments(fd);
      success(t('dropshipping.kycSubmitted'));
      setKycFiles({ kycNicFront: null, kycNicBack: null, kycBusinessRegImage: null });
      loadData();
    } catch (err) {
      showError(err.message || t('dropshipping.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const kycStatus = settings?.kycStatus || 'not_submitted';
  const kycConfig = KYC_STATUS_CONFIG[kycStatus] || KYC_STATUS_CONFIG.not_submitted;
  const KycIcon = kycConfig.icon;

  if (loading) return <div className={styles.loading}>{t('dropshipping.loading')}</div>;

  const showProductsTab = form.isProviderEnabled && settings?.kycStatus === 'approved';

  const tabs = [
    { key: 'provider', label: t('dropshipping.providerSettings') },
    ...(showProductsTab ? [{ key: 'products', label: t('dropshipping.productsTab') }] : []),
    { key: 'reseller', label: t('dropshipping.resellerSettings') },
    { key: 'kyc', label: t('dropshipping.kycDocuments') },
  ];

  return (
    <div className={activeTab === 'products' ? styles.pageWide : styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('dropshipping.settings')}</h1>
        {activeTab !== 'kyc' && activeTab !== 'products' && (
          <button className={styles.saveButton} onClick={handleSaveSettings} disabled={saving}>
            <Save size={16} />
            {saving ? t('dropshipping.loading') : t('dropshipping.saveSettings')}
          </button>
        )}
      </div>

      <div className={styles.tabs}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.tabContent}>
        {activeTab === 'products' && <ProviderProductsTab />}

        {activeTab === 'provider' && (
          <div className={styles.section}>
            <div className={styles.toggleRow}>
              <div>
                <div className={styles.fieldLabel}>{t('dropshipping.enableProvider')}</div>
                <div className={styles.fieldHelp}>{t('dropshipping.enableProviderHelp')}</div>
              </div>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={form.isProviderEnabled}
                  onChange={(e) => handleFieldChange('isProviderEnabled', e.target.checked)}
                />
                <span className={styles.toggleSlider} />
              </label>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.listingVisibility')}</label>
              <div className={styles.selectWrapper}>
                <select
                  className={styles.select}
                  value={form.providerListingVisibility}
                  onChange={(e) => handleFieldChange('providerListingVisibility', e.target.value)}
                >
                  <option value="public">{t('dropshipping.visibilityPublic')}</option>
                  <option value="unlisted">{t('dropshipping.visibilityUnlisted')}</option>
                  <option value="private">{t('dropshipping.visibilityPrivate')}</option>
                </select>
                <ChevronDown className={styles.selectIcon} size={16} />
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.serviceCharge')}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={styles.input}
                value={form.providerServiceCharge}
                onChange={(e) => handleFieldChange('providerServiceCharge', parseFloat(e.target.value) || 0)}
              />
              <span className={styles.fieldHelp}>{t('dropshipping.serviceChargeHelp')}</span>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.providerContactPhone')}</label>
              <input
                type="text"
                className={styles.input}
                value={form.providerContactPhone}
                onChange={(e) => handleFieldChange('providerContactPhone', e.target.value)}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.allowedPaymentCycles')}</label>
              <span className={styles.fieldHelp}>{t('dropshipping.paymentCycleHelp')}</span>
              <div className={styles.cycleCheckboxes}>
                {PAYMENT_CYCLES.map((cycle) => (
                  <label key={cycle} className={styles.cycleLabel}>
                    <input
                      type="checkbox"
                      checked={(form.providerAllowedPaymentCycles || []).includes(cycle)}
                      onChange={() => togglePaymentCycle(cycle)}
                    />
                    {cycle} {t('dropshipping.days')}
                  </label>
                ))}
              </div>
            </div>

            {form.isProviderEnabled && (
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                  <Link size={14} style={{ marginRight: '0.375rem', verticalAlign: 'middle' }} />
                  {t('dropshipping.inviteLink')}
                </label>
                <span className={styles.fieldHelp}>{t('dropshipping.inviteLinkDescription')}</span>

                {settings?.inviteHash ? (() => {
                  const inviteUrl = `${window.location.origin}/join/${settings.inviteHash}`;
                  return (
                    <div className={styles.inviteLinkRow}>
                      <input
                        type="text"
                        className={styles.input}
                        value={inviteUrl}
                        readOnly
                      />
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => handleCopyInviteLink(inviteUrl)}
                        title={t('dropshipping.copyLink')}
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={handleGenerateInviteLink}
                        disabled={generatingLink}
                        title={t('dropshipping.regenerateLink')}
                      >
                        <RefreshCw size={16} />
                      </button>
                    </div>
                  );
                })() : (
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={handleGenerateInviteLink}
                    disabled={generatingLink}
                  >
                    <Link size={16} />
                    {generatingLink ? t('dropshipping.loading') : t('dropshipping.generateLink')}
                  </button>
                )}

                {settings?.inviteHash && (
                  <span className={styles.fieldHelp} style={{ color: 'var(--color-warning)' }}>
                    {t('dropshipping.regenerateWarning')}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'reseller' && (
          <div className={styles.section}>
            <div className={styles.toggleRow}>
              <div>
                <div className={styles.fieldLabel}>{t('dropshipping.enableReseller')}</div>
                <div className={styles.fieldHelp}>{t('dropshipping.enableResellerHelp')}</div>
              </div>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={form.isResellerEnabled}
                  onChange={(e) => handleFieldChange('isResellerEnabled', e.target.checked)}
                />
                <span className={styles.toggleSlider} />
              </label>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.resellerContactPhone')}</label>
              <input
                type="text"
                className={styles.input}
                value={form.resellerContactPhone}
                onChange={(e) => handleFieldChange('resellerContactPhone', e.target.value)}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.selectBank')}</label>
              <div className={styles.selectWrapper}>
                <select
                  className={styles.select}
                  value={form.resellerBankId || ''}
                  onChange={(e) => {
                    handleFieldChange('resellerBankId', e.target.value ? parseInt(e.target.value) : null);
                    handleFieldChange('resellerBankBranchId', null);
                  }}
                >
                  <option value="">{t('dropshipping.selectBank')}</option>
                  {banks.map((bank) => (
                    <option key={bank.id} value={bank.id}>{bank.name}</option>
                  ))}
                </select>
                <ChevronDown className={styles.selectIcon} size={16} />
              </div>
            </div>

            {branches.length > 0 && (
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>{t('dropshipping.selectBranch')}</label>
                <div className={styles.selectWrapper}>
                  <select
                    className={styles.select}
                    value={form.resellerBankBranchId || ''}
                    onChange={(e) => handleFieldChange('resellerBankBranchId', e.target.value ? parseInt(e.target.value) : null)}
                  >
                    <option value="">{t('dropshipping.selectBranch')}</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>{branch.branchName}</option>
                    ))}
                  </select>
                  <ChevronDown className={styles.selectIcon} size={16} />
                </div>
              </div>
            )}

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.accountName')}</label>
              <input
                type="text"
                className={styles.input}
                value={form.resellerBankAccountName}
                onChange={(e) => handleFieldChange('resellerBankAccountName', e.target.value)}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.accountNumber')}</label>
              <input
                type="text"
                className={styles.input}
                value={form.resellerBankAccountNumber}
                onChange={(e) => handleFieldChange('resellerBankAccountNumber', e.target.value)}
              />
            </div>
          </div>
        )}

        {activeTab === 'kyc' && (
          <div className={styles.section}>
            <div className={styles.kycStatusRow}>
              <KycIcon className={`${styles.kycIcon} ${styles[kycConfig.className]}`} size={20} />
              <span className={`${styles.kycStatusLabel} ${styles[kycConfig.className]}`}>
                {t(`dropshipping.${kycConfig.label}`)}
              </span>
            </div>

            {kycStatus === 'rejected' && settings?.kycRejectionReason && (
              <div className={styles.kycRejectionReason}>
                <strong>{t('dropshipping.kycRejectionReason')}:</strong> {settings.kycRejectionReason}
              </div>
            )}

            {kycStatus === 'approved' ? (
              // Read-only view when KYC is approved
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: 'var(--color-bg-secondary)', borderRadius: '8px', marginBottom: '1.25rem', color: 'var(--color-text-muted)' }}>
                  <Lock size={16} />
                  <span style={{ fontSize: '0.9em' }}>{t('dropshipping.kycApprovedLocked') || 'KYC is approved and cannot be modified.'}</span>
                </div>
                {[
                  { field: 'kycNicFront',         label: 'nicFront',         current: settings?.kycNicFront },
                  { field: 'kycNicBack',           label: 'nicBack',          current: settings?.kycNicBack },
                  { field: 'kycBusinessRegImage',  label: 'businessRegImage', current: settings?.kycBusinessRegImage },
                ].map(({ field, label, current }) => current ? (
                  <div key={field} className={styles.kycFileGroup}>
                    <label className={styles.fieldLabel}>{t(`dropshipping.${label}`)}</label>
                    <img
                      src={getImageUrl(current)}
                      alt={t(`dropshipping.${label}`)}
                      style={{ maxHeight: '160px', maxWidth: '100%', objectFit: 'contain', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                    />
                  </div>
                ) : null)}
              </div>
            ) : (
              // Editable upload view
              <>
                {[
                  { field: 'kycNicFront',         label: 'nicFront',         ref: nicFrontRef, required: true,  current: settings?.kycNicFront },
                  { field: 'kycNicBack',           label: 'nicBack',          ref: nicBackRef,  required: true,  current: settings?.kycNicBack },
                  { field: 'kycBusinessRegImage',  label: 'businessRegImage', ref: bizRegRef,   required: false, current: settings?.kycBusinessRegImage },
                ].map(({ field, label, ref, required, current }) => (
                  <div key={field} className={styles.kycFileGroup}>
                    <label className={styles.fieldLabel}>
                      {t(`dropshipping.${label}`)}
                      {required
                        ? <span style={{ color: 'var(--color-danger)', marginLeft: '0.25rem' }}>*</span>
                        : <span style={{ color: 'var(--color-text-muted)', marginLeft: '0.375rem', fontSize: '0.8em' }}>({t('common.optional') || 'optional'})</span>
                      }
                    </label>
                    <div
                      className={`${styles.dropzone} ${(kycFiles[field] || current) ? styles.dropzoneHasFile : ''}`}
                      onClick={() => ref.current?.click()}
                    >
                      {kycPreviews[field] ? (
                        <>
                          <img
                            src={kycPreviews[field]}
                            alt={t(`dropshipping.${label}`)}
                            style={{ maxHeight: '140px', maxWidth: '100%', objectFit: 'contain', borderRadius: '6px' }}
                          />
                          <span style={{ fontSize: '0.8em', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
                            {kycFiles[field]?.name}
                          </span>
                        </>
                      ) : current && !kycFiles[field] ? (
                        <>
                          <img
                            src={getImageUrl(current)}
                            alt={t(`dropshipping.${label}`)}
                            style={{ maxHeight: '140px', maxWidth: '100%', objectFit: 'contain', borderRadius: '6px' }}
                          />
                          <span style={{ fontSize: '0.8em', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
                            {t('dropshipping.clickToReplace') || 'Click to replace'}
                          </span>
                        </>
                      ) : (
                        <>
                          <Upload size={20} />
                          <span>{t('dropshipping.clickToUpload')} {t('dropshipping.orDragDrop')}</span>
                          <small>{t('dropshipping.fileTypes')}</small>
                        </>
                      )}
                    </div>
                    <input
                      ref={ref}
                      type="file"
                      accept="image/*,.pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => handleKycFileChange(field, e.target.files[0] || null)}
                    />
                  </div>
                ))}

                <button
                  className={styles.saveButton}
                  onClick={handleKycUpload}
                  disabled={saving || !kycFiles.kycNicFront || !kycFiles.kycNicBack}
                >
                  <Upload size={16} />
                  {saving ? t('dropshipping.loading') : t('dropshipping.kycDocuments')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DropshippingSettingsPage;
